<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-breadcrumbs class="ma-0 pa-0" v-if="!selectedCycleSet" :items="cycleSetsHeaderItems" divider=">">
          </v-breadcrumbs>

          <v-breadcrumbs class="ma-0 pa-0" v-if="selectedCycleSet" :items="cycleHeaderItems" divider=">">
            <template v-slot:item="{ item }">
              <v-breadcrumbs-item :disabled="item.disabled" :link= "item.link"
                :href=  "'#/'+$route.params.clientId+'/cyclesets'"
                @click="goTo(item.text)">
                {{ item.text }}
              </v-breadcrumbs-item>
            </template>
          </v-breadcrumbs>

        </v-col>
      </v-row>
    </v-container>

    <v-container v-if="loading">
      <v-progress-circular indeterminate size="50" />
    </v-container>

    <v-container v-if="!loading && cycleSets && !selectedCycleSet">
      <!-- Cycle Sets -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h6">Cycle Sets</span>
            </v-card-title>
            <v-card-text>
              <v-container>

                <!-- Cycle sets -->
                <v-data-table
                  :headers="cycleSetsHeaders"
                  :items="cycleSets"
                  :loading="loading"
                  :hide-default-footer="true"
                  fixed-header
                  ref="cycleSetsTable">
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>
                        <cycle-set-dialog
                          :cycleSet="item"
                          @success="onCyclesSetSaved"
                          :existingCycleSets="cycleSets"
                          :hasProcessingDays="hasProcessingDays"
                          isEditButton="true"/>
                      </td>
                      <td>{{ item.name }}</td>
                      <td>{{ getCycleCodes(item) }}</td>
                      <td>
                        <v-btn
                          :ref="'viewButton'+item.id"
                          @click="viewSelectedCycleSet(item)"
                          >View Cycles
                        </v-btn>
                      </td>
                      <td>
                        <v-btn icon @click="onDeleteCyclesSet(item)" :ref="'deleteIcon'+item.id">
                          <v-icon>fa-trash</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </template>
                </v-data-table>

                <!-- Add new button -->
                <v-col>
                  <cycle-set-dialog
                    @success="onCyclesSetSaved"
                    :hasProcessingDays="hasProcessingDays"
                    :existingCycleSets="cycleSets"/>
                </v-col>

              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-if="!loading && cycleSets && selectedCycleSet">
      <!-- Cycle Set Dropdown -->
      <v-row>
        <v-col cols="12">
          <v-card class="dense">
            <v-card-text>
              <v-row>
                <v-col>
                  <v-autocomplete
                    label="Cycle Set Name"
                    :items="cycleSets"
                    item-text="name"
                    item-value="id"
                    v-model="selectedCycleSet"
                    @change="updateCurrentAndPastCycles()"
                    return-object
                    />
                </v-col>
                <v-col>
                  <v-btn
                    ref="backButton"
                    @click="viewAllCycleSets()"
                    color="primary"
                    >Back
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Cycle Set desc -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h6">Cycle Set</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-data-table
                  :headers="cycleSetsHeaders.slice(1,3)"
                  :footer-props="footerProps"
                  :hide-default-footer="true"
                  :items="[selectedCycleSet]"
                  dense
                  fixed-header>
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>{{ item.name }}</td>
                      <td>{{getCycleCodes(item)}}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Current & Upcoming Cycles -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h6">Current & Upcoming Cycles</span>
            </v-card-title>
            <v-card-text>
              <v-container>

                <!-- Display warnings -->
                <v-col v-if="warnings.length>0" style="background-color: rgb(243, 245, 195)">
                  <span v-for="item in warnings" :key="item.warningType + '_top'">
                      <gap-warning :warning="item" /> <br>
                  </span>
                </v-col>

                <v-data-table
                  :headers="cycleHeader"
                  :footer-props="footerProps"
                  :hide-default-footer="false"
                  :items="currentCyclesInSelectedCycleSet"
                  fixed-header
                  dense
                  ref="currentAndUpcomingCyclesTable">
                  <template v-slot:header="" >
                    <thead>
                      <tr>
                        <th colspan="3"></th>
                        <th colspan="2">Statement</th>
                        <th colspan="2">Qualifications</th>
                        <th colspan="2">Earnings</th>
                        <th colspan=""></th>
                      </tr>
                    </thead>
                  </template>

                  <template v-slot:item="{ item }">
                    <tr>
                      <td><cycle-dialog
                          :cycle="item"
                          :hasProcessingDays = "hasProcessingDays"
                          @success="onCycleSaved"
                          :existingCycles="currentCyclesInSelectedCycleSet"
                          isEditButton="true"/></td>
                      <td>{{ getFriendlyFormattedDate(item.dropDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.secondDropDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.statementStartDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.statementEndDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.qualificationStartDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.qualificationEndDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.earningsStartDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.earningsEndDate) }}</td>
                      <td>
                        <v-btn icon @click="onDeleteCycle(item)" :ref="'delete'+item.id">
                          <v-icon>fa-trash</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </template>
                </v-data-table>

                <!-- Display warnings -->
                <v-col v-if="warnings.length>0" style="background-color: rgb(243, 245, 195)">
                  <span v-for="item in warnings" :key="item.warningType + '_bottom'">
                      <gap-warning :warning="item" /> <br>
                  </span>
                </v-col>

                <!-- Add new button-->
                <v-col>
                  <cycle-dialog
                      :cycle="getLastCurrentCycle()"
                      :cycleSetId="selectedCycleSet.id"
                      :hasProcessingDays = "hasProcessingDays"
                      @success="onCycleSaved"
                  />
                </v-col>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Past Cycles -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h6">Past Cycles</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-data-table
                  :headers="cycleHeader.slice(1,9)"
                  :footer-props="footerProps"
                  :hide-default-footer="false"
                  :items="pastCyclesInSelectedCycleSet"
                  fixed-header
                  dense
                  ref="pastCyclesTable">
                  <template v-slot:header="" >
                    <thead>
                      <tr>
                        <th colspan="2"></th>
                        <th colspan="2">Statement</th>
                        <th colspan="2">Qualifications</th>
                        <th colspan="2">Earnings</th>
                      </tr>
                    </thead>
                  </template>

                  <template v-slot:item="{ item }">
                    <tr>
                      <td>{{ getFriendlyFormattedDate(item.dropDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.secondDropDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.statementStartDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.statementEndDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.qualificationStartDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.qualificationEndDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.earningsStartDate) }}</td>
                      <td>{{ getFriendlyFormattedDate(item.earningsEndDate) }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </v-container>

  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { AxiosResponse } from 'axios';
import {
  Component, Inject, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import { Alert, NoticeClass, Dialog } from '@kasasa/fbase-components/lib';
import CycleSetDialog from '@/components/CycleSetDialog.vue';
import CycleDialog from '@/components/CycleDialog.vue';
import GapWarning from '@/components/gapwarning/GapWarning.vue';
import { GapWarningInfo, IGapWarningConfig } from '@/api/gapwarning';

import {
  Cycle,
  CycleSet,
  CycleResource,
} from '@/api/cycles';
import { ProcessingDays, ProcessingDaysResource } from '@/api/processingdays';

@Component({
  computed: mapState(['fiId']),
  components: {
    'cycle-set-dialog': CycleSetDialog,
    'cycle-dialog': CycleDialog,
    'gap-warning': GapWarning,
  },
})
export default class CycleSets extends Vue {
  @Inject('CycleResource')
  cycleResource!: CycleResource;

  @Inject('ProcessingDaysResource')
  processingDaysResource!: ProcessingDaysResource;

  fiId!: string;

  loading: boolean = false;

  cycleSets: CycleSet[] = [];

  selectedCycleSet: CycleSet|null = null;

  currentCyclesInSelectedCycleSet: Cycle[] = [];

  pastCyclesInSelectedCycleSet: Cycle[] = [];

  static ALL_STATEMENT_CODE = 'All Statement Cycle Codes';

  warnings: IGapWarningConfig[] = [];

  hasProcessingDays: boolean = false;

  cycleSetsHeaders:DataTableHeader[] = [
    { text: '', value: 'edit', sortable: false },
    { text: 'Cycle Set Name', value: 'name', sortable: false },
    { text: 'Statement Cycle Codes', value: 'statementCycleCodes', sortable: false },
    { text: '', value: 'deleteIcon', sortable: false },
  ];

  cycleHeader:DataTableHeader[] = [
    { text: '', value: 'edit', sortable: false },
    { text: 'Drop Date', value: 'dropDate', sortable: false },
    { text: '2nd Drop Date', value: 'secondDropDate', sortable: false },
    { text: 'Start', value: 'statementStartDate', sortable: false },
    { text: 'End', value: 'statementEndDate', sortable: false },
    { text: 'Start', value: 'qualificationStartDate', sortable: false },
    { text: 'End', value: 'qualificationEndDate', sortable: false },
    { text: 'Start', value: 'earningsStartDate', sortable: false },
    { text: 'End', value: 'earningsEndDate', sortable: false },
    { text: '', value: 'trashIcon', sortable: false },
  ];

  footerProps = {
    'items-per-page-options': [{ value: -1, text: 'All' }, 25, 50, 100],
  }

  cycleSetsHeaderItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: true,
      link: false,
    },
    {
      text: 'Cycle Sets',
      disabled: true,
      link: false,
    },
  ];

  cycleHeaderItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: true,
      link: false,
    },
    {
      text: 'Cycle Sets',
      disabled: false,
      link: true,
    },
    {
      text: 'Cycle',
      disabled: true,
      link: false,
    },
  ];

  mounted() {
    this.loading = true;
    this.processingDaysResource.getAll(Number(this.fiId))
      .then((result:ProcessingDays) => {
        if (result === null || result === undefined || result.normalProcessingDays === undefined || result.normalProcessingDays.length < 4) {
          this.hasProcessingDays = false;
          this.loading = false;
          this.setError('Processing Days must be configured before configuring Cycle Sets.');
        } else {
          this.hasProcessingDays = true;
          this.cycleResource.getAllCycleSets(this.fiId)
            .then((result1:CycleSet[]) => {
              this.cycleSets = result1;
              this.loading = false;
            })
            .catch((error:any) => {
              if (this.hasProcessingDays) {
                this.showError(error);
              }
              this.loading = false;
            });
        }
      })
      .catch((error:any) => {
        this.setError('Processing Days must be configured before configuring Cycle Sets.');
        this.hasProcessingDays = false;
        this.loading = false;
      });
  }

  getLastCurrentCycle() {
    return this.currentCyclesInSelectedCycleSet[this.currentCyclesInSelectedCycleSet.length - 1];
  }

  viewSelectedCycleSet(cycleSet: CycleSet) {
    this.currentCyclesInSelectedCycleSet = this.getCurrentCycles(cycleSet.cyclesDesc);
    this.pastCyclesInSelectedCycleSet = this.getPastCycles(cycleSet.cyclesDesc);
    this.selectedCycleSet = cycleSet;
  }

  viewAllCycleSets() {
    this.currentCyclesInSelectedCycleSet = [];
    this.pastCyclesInSelectedCycleSet = [];
    this.selectedCycleSet = null;
  }

  goTo(breadcrumbItem: string) {
    if (breadcrumbItem === 'Cycle Sets') {
      this.viewAllCycleSets();
    }
  }

  updateCurrentAndPastCycles() {
    this.currentCyclesInSelectedCycleSet = this.getCurrentCycles(this.selectedCycleSet!.cyclesDesc);
    this.pastCyclesInSelectedCycleSet = this.getPastCycles(this.selectedCycleSet!.cyclesDesc);
  }

  onDeleteCyclesSet(cycleSet: CycleSet) {
    const dialog = new Dialog('Confirm Deletion',
      'Are you sure you want to delete this cycle set and all associated cycles?');
    dialog.setAcceptLabel('OK')
      .setDeclineLabel('Cancel');
    this.$store.dispatch('notices/add', dialog)
      .then((ok) => {
        if (ok === 'accept') {
          this.cycleResource.removeCycleSet(this.fiId, cycleSet.id!)
            .then((result:AxiosResponse) => {
              this.removeCycleSetFromList(cycleSet);
            });
        }
      })
      .catch((error:any) => {
        if (error && error.response && error.response.status === 422) {
          this.setError('CycleSet cannot be deleted because it has associated Account Results');
        } else {
          this.setError('Unexpected error in deleting CycleSet');
        }
      });
  }

  removeCycleSetFromList(cycleSet: CycleSet) {
    for (let i:number = 0; i < this.cycleSets.length; i++) {
      if (this.cycleSets[i].id === cycleSet.id) {
        this.cycleSets.splice(i, 1);
        break;
      }
    }
    this.showSuccess(`Deleted Cycle Set: ${cycleSet.name}`);
  }

  onDeleteCycle(cycle: Cycle) {
    const dialog = new Dialog('Confirm Deletion',
      'Are you sure you want to delete this cycle?');
    dialog.setAcceptLabel('OK')
      .setDeclineLabel('Cancel');
    this.$store.dispatch('notices/add', dialog)
      .then((ok) => {
        if (ok === 'accept') {
          this.cycleResource.removeCycle(this.fiId, cycle.id!)
            .then((result:AxiosResponse) => {
              this.removeCycleFromList(cycle);
            });
        }
      })
      .catch((error) => {
        if (error && error.response && error.response.status === 422) {
          this.setError('Cycle cannot be deleted because it has associated Account Results');
        } else {
          this.setError('Unexpected error in deleting Cycle');
        }
      });
  }

  removeCycleFromList(cycle: Cycle) {
    for (let i:number = 0; i < this.selectedCycleSet!.cyclesDesc.length; i++) {
      if (this.selectedCycleSet!.cyclesDesc[i].id === cycle.id) {
        this.selectedCycleSet!.cyclesDesc.splice(i, 1);
        break;
      }
    }

    this.currentCyclesInSelectedCycleSet = this.getCurrentCycles(this.selectedCycleSet!.cyclesDesc);
    this.showSuccess(`Deleted Cycle with drop date: ${
      this.getFriendlyFormattedDate(cycle.dropDate)}`);
  }

  onCyclesSetSaved(savedCycleSet: CycleSet) {
    let found:boolean = false;
    for (let i:number = 0; i < this.cycleSets.length; i++) {
      if (this.cycleSets[i].id === savedCycleSet.id) {
        // Saved an existing cycle set, so make sure that it is up to date.
        Object.assign(this.cycleSets[i], savedCycleSet);
        found = true;
        break;
      }
    }
    if (!found) {
      const cycleSet = savedCycleSet;
      this.cycleSets.push(cycleSet);
    }
    this.showSuccess(`Saved Cycle Set: ${savedCycleSet.name}`);
  }

  onCycleSaved(savedCycle: Cycle) {
    let found:boolean = false;
    for (let j:number = 0; j < this.selectedCycleSet!.cyclesDesc.length; j++) {
      if (this.selectedCycleSet!.cyclesDesc[j].id === savedCycle.id) {
        // Saved an existing cycle, so make sure that it is up to date.
        Object.assign(this.selectedCycleSet!.cyclesDesc[j], savedCycle);
        found = true;
        break;
      }
    }
    if (!found) {
      const cycle = savedCycle;
      this.selectedCycleSet!.cyclesDesc.push(cycle);
    }

    this.updateCurrentAndPastCycles();
    this.showSuccess(`Saved Cycle with drop date: ${
      this.getFriendlyFormattedDate(savedCycle.dropDate)}`);
  }

  getCycleCodes = (cycleSet: CycleSet) => {
    const cycleCodes:string[] = [];
    Object.assign(cycleCodes, cycleSet.statementCycleCodes);
    if (cycleSet.processAllStatementCycleCodes) {
      return CycleSets.ALL_STATEMENT_CODE;
    }
    return cycleCodes.sort().join('; ');
  }

  getFriendlyFormattedDate = (date: string) => {
    const [year, month, day] = date.split('-');
    return `${month}/${day}/${year}`;
  }

  getCurrentCycles(cycles: Cycle[]) {
    const currentCycles: Cycle[] = [];
    const today = this.todayDate();
    cycles.forEach((cycle) => {
      if (this.parseDate(cycle.secondDropDate) >= today) {
        currentCycles.push(cycle);
      }
    });
    this.checkForGaps(currentCycles);
    return this.sortCyclesByDropDate(currentCycles).reverse();
  }

  getPastCycles(cycles: Cycle[]) {
    const filteredCycles: Cycle[] = [];
    const today = this.todayDate();
    cycles.forEach((cycle) => {
      if (this.parseDate(cycle.secondDropDate) < today) {
        filteredCycles.push(cycle);
      }
    });
    return this.sortCyclesByDropDate(filteredCycles);
  }

  sortCyclesByDropDate = (cycles: Cycle[]) => cycles.sort((a, b) => new Date(b.dropDate).valueOf() - new Date(a.dropDate).valueOf())

  checkForGaps(currentCycles: Cycle[]) {
    this.warnings = [];
    if (currentCycles.length < 1) {
      return;
    }

    // Sort by statement date.  We will re-sort before each check, as it's possible (however unlikely)
    // that different values of statement/qualification/earnings start dates could result
    // in different orderings.
    currentCycles.sort((cycle1, cycle2) => new Date(cycle1.statementStartDate).getTime() - new Date(cycle2.statementStartDate).getTime());
    for (let i = 1; i < currentCycles.length; i++) {
      this.checkForGapBeforeStatementStart(currentCycles[i - 1], currentCycles[i]);
    }

    currentCycles.sort((cycle1, cycle2) => new Date(cycle1.qualificationStartDate).getTime() - new Date(cycle2.qualificationStartDate).getTime());
    for (let i = 1; i < currentCycles.length; i++) {
      this.checkForGapBeforeQualificationStart(currentCycles[i - 1], currentCycles[i]);
    }

    currentCycles.sort((cycle1, cycle2) => new Date(cycle1.earningsStartDate).getTime() - new Date(cycle2.earningsStartDate).getTime());
    for (let i = 1; i < currentCycles.length; i++) {
      this.checkForGapBeforeEarningsStart(currentCycles[i - 1], currentCycles[i]);
    }
  }

  checkForGapBeforeStatementStart(previousCycle: Cycle, currentCycle: Cycle) {
    if (this.addOneDay(previousCycle.statementEndDate) !== currentCycle.statementStartDate) {
      this.warnings.push(new GapWarningInfo('statement', currentCycle.statementStartDate, previousCycle.statementEndDate));
    }
  }

  checkForGapBeforeQualificationStart(previousCycle: Cycle, currentCycle: Cycle) {
    if (this.addOneDay(previousCycle.qualificationEndDate) !== currentCycle.qualificationStartDate) {
      this.warnings.push(new GapWarningInfo('qualification', currentCycle.qualificationStartDate, previousCycle.qualificationEndDate));
    }
  }

  checkForGapBeforeEarningsStart(previousCycle: Cycle, currentCycle: Cycle) {
    if (this.addOneDay(previousCycle.earningsEndDate) !== currentCycle.earningsStartDate) {
      this.warnings.push(new GapWarningInfo('earnings', currentCycle.earningsStartDate, previousCycle.earningsEndDate));
    }
  }

  parseDate = (date: string) => {
    const parts = date.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }

  todayDate = () => {
    const daDate = new Date();
    daDate.setHours(0, 0, 0, 0);
    return daDate;
  }

  addOneDay(dateString: string) {
    const date = this.parseDate(dateString);
    date.setDate(date.getDate() + 1);
    return date.toISOString().substring(0, 10);
  }

  showError(error: any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message: string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  showSuccess(message: string) {
    const al = new Alert(message, NoticeClass.SUCCESS);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss">
</style>
